// GET /api/documents/unmatched and its assign/resource mutations - the
// inbox queue. Deliberately no fixture fallback: an empty queue is the
// real state until a Shared Drive inbox feeds it, so the page owns honest
// empty and error screens instead.

import { getAuthHeaders } from "./auth";
import { clientNameOf, type ServerCaseShape } from "./mock";

export interface SuggestedCase {
  _id: string;
  eId: string | null;
  caseNumber: string | null;
  clientName: string;
}

export interface UnmatchedDoc {
  _id: string;
  fileName: string;
  source?: string;
  createdAt?: string;
  aiSummary: string | null;
  // Present when the inbox processor found a 0.7-0.9-confidence match:
  // the document stays in the queue but carries its suggestion.
  suggestedCase: SuggestedCase | null;
}

export interface UnmatchedResult {
  documents: UnmatchedDoc[];
  total: number;
}

// The server's list route caps limit at 100.
const QUEUE_PAGE = 100;

export async function fetchUnmatched(): Promise<UnmatchedResult> {
  const res = await fetch(`/api/documents/unmatched?limit=${QUEUE_PAGE}`, {
    headers: { ...getAuthHeaders() },
    credentials: "same-origin",
  });
  if (!res.ok) throw new Error(`GET /api/documents/unmatched failed: ${res.status}`);
  return (await res.json()) as UnmatchedResult;
}

export async function assignUnmatched(docId: string, caseId: string): Promise<void> {
  const res = await fetch(`/api/documents/unmatched/${encodeURIComponent(docId)}/assign`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...getAuthHeaders() },
    credentials: "same-origin",
    body: JSON.stringify({ caseId }),
  });
  if (!res.ok) throw new Error(`Assign failed: ${res.status}`);
}

// The resource route takes NO body and has no schema - send none.
export async function saveAsResource(docId: string): Promise<void> {
  const res = await fetch(`/api/documents/unmatched/${encodeURIComponent(docId)}/resource`, {
    method: "POST",
    headers: { ...getAuthHeaders() },
    credentials: "same-origin",
  });
  if (!res.ok) throw new Error(`Save as resource failed: ${res.status}`);
}

// Case picker for assignment. Reads /api/cases?search= directly because the
// assign route needs the Mongo _id, which adaptCaseToMatter drops.
export interface AssignTarget {
  _id: string;
  eId: string | null;
  clientName: string | null;
  caseType?: string;
}

export async function searchAssignTargets(q: string): Promise<AssignTarget[]> {
  const res = await fetch(`/api/cases?search=${encodeURIComponent(q)}&limit=8`, {
    headers: { ...getAuthHeaders() },
    credentials: "same-origin",
  });
  if (!res.ok) throw new Error(`GET /api/cases?search failed: ${res.status}`);
  const json = (await res.json()) as { cases?: ServerCaseShape[] };
  return (json.cases ?? [])
    .filter((c) => c.status !== "closed" && c.status !== "archived")
    .map((c) => ({
      _id: c._id,
      eId: c.eId ?? null,
      clientName: clientNameOf(c) ?? null,
      caseType: c.caseType,
    }));
}
